const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

interface CompressOptions {
  maxDimension?: number
  quality?: number
  minSizeToCompress?: number
}

const DEFAULTS: Required<CompressOptions> = {
  maxDimension: 4096,
  quality: 0.88,
  minSizeToCompress: 5 * 1024 * 1024, // 5MB
}

/**
 * Comprime imagens (PNG/JPG/WEBP) → JPEG com Canvas API.
 * Retorna o File original se não for imagem ou estiver abaixo do threshold.
 */
export async function compressImage(
  file: File,
  opts?: CompressOptions,
): Promise<{ file: File; compressed: boolean; originalSize: number }> {
  if (!IMAGE_TYPES.includes(file.type)) {
    return { file, compressed: false, originalSize: file.size }
  }

  const { maxDimension, quality, minSizeToCompress } = { ...DEFAULTS, ...opts }
  if (file.size < minSizeToCompress) {
    return { file, compressed: false, originalSize: file.size }
  }

  const originalSize = file.size
  return new Promise((resolve) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round(height * maxDimension / width)
          width = maxDimension
        } else {
          width = Math.round(width * maxDimension / height)
          height = maxDimension
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        (blob) => {
          const baseName = file.name.replace(/\.[^.]+$/, '')
          resolve({
            file: new File([blob!], `${baseName}.jpg`, { type: 'image/jpeg' }),
            compressed: true,
            originalSize,
          })
        },
        'image/jpeg',
        quality,
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      resolve({ file, compressed: false, originalSize: file.size })
    }
    img.src = url
  })
}
